import { useContext, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { AuthSessionContext } from '@context/AuthContext'

import { useAccountAccessStatuses } from '@utils/AccountAccess/accountAccessStatuses'
import { useAccountVisibilityStatuses } from '@utils/AccountAccess/accountVisibilityStatuses'

import { isVerificationExpedited } from '@model/AccountDetailedDto'
import { AccountDetailedStatus } from '@model/AccountDetailedStatus'

export type LayoutShell = 'minimal-anon' | 'minimal-auth' | 'full'

export type LayoutMenu = 'visible' | 'hidden'

export type LayoutContent = 'container' | 'fluid' | 'flush' | 'semi'

export type LayoutNotices = 'visible' | 'hidden'

interface BaseLayoutConfig {
  menu: LayoutMenu
  topbar: LayoutMenu
  content: LayoutContent
  notices: LayoutNotices
}

export interface MinimalAnonLayoutConfig extends BaseLayoutConfig {
  shell: 'minimal-anon'
}

export interface MinimalAuthLayoutConfig extends BaseLayoutConfig {
  shell: 'minimal-auth'
}

export interface FullLayoutConfig extends BaseLayoutConfig {
  shell: 'full'
}

export type LayoutConfig = MinimalAnonLayoutConfig | MinimalAuthLayoutConfig | FullLayoutConfig

export type LayoutPreset =
  | 'minimal-anon'
  | 'minimal-auth'
  | 'minimal-auth-no-container'
  | 'full'
  | 'full-no-menu'
  | 'full-no-menu-padded'
  | 'full-no-container'
  | 'full-flush'

export type DefaultLayoutPreset = Extract<LayoutPreset, 'minimal-anon' | 'minimal-auth' | 'full'>

export type LayoutDefinition = LayoutPreset | Partial<LayoutConfig>

const LAYOUT_PRESET_CONFIGS = {
  'minimal-anon': {
    shell: 'minimal-anon',
    menu: 'hidden',
    topbar: 'visible',
    content: 'container',
    notices: 'hidden',
  },
  'minimal-auth': {
    shell: 'minimal-auth',
    menu: 'hidden',
    topbar: 'visible',
    content: 'container',
    notices: 'hidden',
  },
  full: {
    shell: 'full',
    menu: 'visible',
    topbar: 'visible',
    content: 'container',
    notices: 'visible',
  },
  'full-no-menu': {
    shell: 'full',
    menu: 'hidden',
    topbar: 'hidden',
    content: 'flush',
    notices: 'hidden',
  },
  'full-no-menu-padded': {
    shell: 'full',
    menu: 'hidden',
    topbar: 'hidden',
    content: 'fluid',
    notices: 'hidden',
  },
  'full-no-container': {
    shell: 'full',
    menu: 'visible',
    topbar: 'visible',
    content: 'semi',
    notices: 'hidden',
  },
  'full-flush': {
    shell: 'full',
    menu: 'visible',
    topbar: 'visible',
    content: 'flush',
    notices: 'hidden',
  },
  'minimal-auth-no-container': {
    shell: 'minimal-auth',
    menu: 'hidden',
    topbar: 'visible',
    content: 'flush',
    notices: 'hidden',
  },
} as const satisfies Record<LayoutPreset, LayoutConfig>

const getLayoutConfigForPreset = (preset: LayoutPreset): LayoutConfig => ({
  ...LAYOUT_PRESET_CONFIGS[preset],
})

export const resolveLayoutDefinition = (
  basePreset: DefaultLayoutPreset,
  layout?: LayoutDefinition
): LayoutConfig => {
  const baseConfig = getLayoutConfigForPreset(basePreset)

  if (!layout) {
    return baseConfig
  }

  if (typeof layout === 'string') {
    return getLayoutConfigForPreset(layout)
  }

  return {
    ...baseConfig,
    ...layout,
  }
}

const ANON_PATH_PREFIXES = [
  '/login',
  '/logout',
  '/forgot-password',
  '/sign-up',
  '/blocked',
  '/users/',
  '/2fa/',
  '/marketing-form',
]

const isAnonPath = (pathname: string): boolean =>
  ANON_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix)) || pathname === '/'

export function useDefaultLayoutPreset(): DefaultLayoutPreset {
  const [auth] = useContext(AuthSessionContext)
  const { account } = useAccountReadContext()
  const { pathname } = useLocation()

  const { isAccountActivatedStatus } = useAccountAccessStatuses()
  const { isAccountVisibilityLockUnlockStatus } = useAccountVisibilityStatuses()

  return useMemo<DefaultLayoutPreset>(() => {
    if (isAnonPath(pathname)) {
      return 'minimal-anon'
    }

    if (!auth) {
      return 'minimal-anon'
    }

    if (!account || !isAccountActivatedStatus) {
      return 'minimal-auth'
    }

    if (isAccountActivatedStatus) {
      return 'full'
    }

    const isExpedited = isVerificationExpedited(account)
    const isAppTestSubmitted =
      account.status?.id === AccountDetailedStatus.AppropriatenessTestSubmitted

    if (isAppTestSubmitted && isExpedited) {
      return 'full'
    }

    if (isAccountVisibilityLockUnlockStatus) {
      return 'full'
    }

    return 'minimal-auth'
  }, [pathname, auth, account, isAccountActivatedStatus, isAccountVisibilityLockUnlockStatus])
}
